import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Heart,
  KeyRound,
  Loader2,
} from "lucide-react";
import { notify } from "@/components/common/Notification";
import {
  donationsAPI,
  statesAPI,
  districtsAPI,
  donationSchemesAPI,
  donationMasterAPI,
  donorAuthAPI,
  DonorLoginRequiredError,
  type DonationCreateOrderPayload,
  type DonationAddPayload,
  type DonorOtpMode,
  type DonationMaster,
  type State,
  type District,
  type City,
} from "../../../services/api";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../../ui/input-otp";
import { openRazorpayCheckout, getRazorpayKey } from "../../RazorpayPayment";
import { DEFAULT_PRESET_AMOUNTS } from "../../../donation/data";
import DonationDetailSidebar from "../../donation/DonationDetailSidebar";
import {
  getCampaignStatusLabel,
  schemeToCampaign,
} from "../../../donation/mappers";
import type { DonationCampaign } from "../../../donation/types";
import {
  formatInr,
  getAmountStillNeeded,
  getContributionAmounts,
  getFundingProgress,
} from "../../../donation/utils";
import PaymentVerificationOverlay, {
  type PaymentVerificationStatus,
} from "../../donation/PaymentVerificationOverlay";

type Step = 1 | 2 | 3;

const OTP_RESEND_SECONDS = 90;

function formatOtpTimer(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
}

const UPI_ID = "jbf@hdfcbank";

interface DonorDetails {
  name: string;
  email: string;
  phone: string;
  organization: string;
  addressLine1: string;
  addressLine2: string;
  state_id: string;
  district_id: string;
  city_id: string;
  pincode: string;
  panNumber: string;
  message: string;
}

const initialDetails: DonorDetails = {
  name: "",
  email: "",
  phone: "",
  organization: "",
  addressLine1: "",
  addressLine2: "",
  state_id: "",
  district_id: "",
  city_id: "",
  pincode: "",
  panNumber: "",
  message: "",
};

const detailInputClass =
  "w-full rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2";
const detailInputOk =
  "border-gray-200 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20";
const detailInputErr = "border-red-500";

export default function DonationDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<DonationCampaign | null>(null);
  const [loadingCampaign, setLoadingCampaign] = useState(true);

  const contributionAmounts = useMemo(
    () => getContributionAmounts(campaign),
    [campaign]
  );
  const [step, setStep] = useState<Step>(1);
  const [amount, setAmount] = useState(DEFAULT_PRESET_AMOUNTS[0]);
  const [customAmount, setCustomAmount] = useState(
    String(DEFAULT_PRESET_AMOUNTS[0])
  );
  const [details, setDetails] = useState<DonorDetails>(initialDetails);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] =
    useState<PaymentVerificationStatus>("idle");
  const [copiedUpi, setCopiedUpi] = useState(false);

  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [detailErrors, setDetailErrors] = useState<
    Partial<Record<keyof DonorDetails, string>>
  >({});
  const [step1Mobile, setStep1Mobile] = useState("");
  const [showOtpField, setShowOtpField] = useState(false);
  const [otp, setOtp] = useState("");
  const [codeReference, setCodeReference] = useState("");
  const [otpAuthMode, setOtpAuthMode] = useState<DonorOtpMode>("register");
  const [mobileFlowLoading, setMobileFlowLoading] = useState(false);
  const [otpResendSeconds, setOtpResendSeconds] = useState(0);
  const [otpTimerKey, setOtpTimerKey] = useState(0);
  const [resendingOtp, setResendingOtp] = useState(false);

  useEffect(() => {
    if (!showOtpField) {
      setOtpResendSeconds(0);
      return;
    }

    setOtpResendSeconds(OTP_RESEND_SECONDS);
    const intervalId = window.setInterval(() => {
      setOtpResendSeconds(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [showOtpField, otpTimerKey]);

  const restartOtpTimer = useCallback(() => {
    setOtpTimerKey(key => key + 1);
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const list = await statesAPI.list();
        setStates(list);
      } catch (err) {
        console.error("Failed to load states:", err);
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, []);

  useEffect(() => {
    if (!campaignId) {
      setLoadingCampaign(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingCampaign(true);
      try {
        const scheme = await donationSchemesAPI.get(campaignId);
        if (cancelled) {
          return;
        }
        if (scheme) {
          const mapped = schemeToCampaign(scheme);
          setCampaign(mapped);
          const initial =
            getContributionAmounts(mapped)[0] ?? DEFAULT_PRESET_AMOUNTS[0];
          setAmount(initial);
          setCustomAmount(String(initial));
        } else {
          setCampaign(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          setCampaign(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingCampaign(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!details.state_id) {
        setDistricts([]);
        return;
      }
      setLoadingDistricts(true);
      try {
        const list = await statesAPI.getDistrictsByStateId(details.state_id);
        setDistricts(list);
      } catch (err) {
        console.error("Failed to load districts:", err);
      } finally {
        setLoadingDistricts(false);
      }
    };
    loadDistricts();
  }, [details.state_id]);

  useEffect(() => {
    const loadCities = async () => {
      if (!details.district_id) {
        setCities([]);
        return;
      }
      setLoadingCities(true);
      try {
        const list = await districtsAPI.getCitiesByDistrictId(
          details.district_id
        );
        setCities(list);
      } catch (err) {
        console.error("Failed to load cities:", err);
      } finally {
        setLoadingCities(false);
      }
    };
    loadCities();
  }, [details.district_id]);

  const handleDetailChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    let { value } = e.target;
    const { name } = e.target;

    if (name === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    if (name === "pincode") {
      value = value.replace(/\D/g, "").slice(0, 6);
    }

    if (name === "state_id") {
      setDetails(prev => ({
        ...prev,
        state_id: value,
        district_id: "",
        city_id: "",
      }));
    } else if (name === "district_id") {
      setDetails(prev => ({
        ...prev,
        district_id: value,
        city_id: "",
      }));
    } else {
      setDetails(prev => ({ ...prev, [name]: value }));
    }

    if (detailErrors[name as keyof DonorDetails]) {
      setDetailErrors(prev => {
        const next = { ...prev };
        delete next[name as keyof DonorDetails];
        return next;
      });
    }
  };

  const selectAmount = (value: number) => {
    setAmount(value);
    setCustomAmount(String(value));
  };

  const handleCustomAmountChange = (value: string) => {
    const cleaned = value.replace(/[^\d]/g, "");
    setCustomAmount(cleaned);
    const num = Number(cleaned);
    if (Number.isFinite(num) && num > 0) {
      setAmount(num);
    }
  };

  const progress = campaign
    ? getFundingProgress(campaign.raised, campaign.goal)
    : 0;
  const stillNeeded = campaign
    ? getAmountStillNeeded(campaign.raised, campaign.goal)
    : 0;

  const handleCopyUpi = async () => {
    try {
      await navigator.clipboard.writeText(UPI_ID);
      setCopiedUpi(true);
      setTimeout(() => setCopiedUpi(false), 2000);
    } catch {
      notify.error("Copy failed", "Could not copy UPI ID.");
    }
  };

  const validateStep1 = () => {
    if (!amount || amount <= 0) {
      notify.error("Invalid amount", "Please select or enter a valid amount.");
      return false;
    }
    if (campaign?.amountType === "variable") {
      const min = campaign.minContribution;
      const max = campaign.maxContribution;
      if (min !== null && min !== undefined && amount < min) {
        notify.error(
          "Invalid amount",
          `Minimum contribution is ${formatInr(min)}.`
        );
        return false;
      }
      if (max !== null && max !== undefined && amount > max) {
        notify.error(
          "Invalid amount",
          `Maximum contribution is ${formatInr(max)}.`
        );
        return false;
      }
    }
    return true;
  };

  const validateStep2 = () => {
    const newErrors: Partial<Record<keyof DonorDetails, string>> = {};

    if (!details.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!details.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(details.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!details.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (details.phone.length !== 10) {
      newErrors.phone = "Phone number must be 10 digits";
    }
    if (!details.addressLine1.trim()) {
      newErrors.addressLine1 = "Address Line 1 is required";
    }
    if (!details.state_id) {
      newErrors.state_id = "State is required";
    }
    if (!details.district_id) {
      newErrors.district_id = "District is required";
    }
    if (!details.city_id) {
      newErrors.city_id = "City is required";
    }
    if (!details.pincode.trim() || details.pincode.length !== 6) {
      newErrors.pincode = "Valid 6-digit pincode is required";
    }
    if (!details.panNumber.trim()) {
      newErrors.panNumber =
        amount > 2000
          ? "PAN Number is required for donation above ₹2000"
          : "PAN Number is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setDetailErrors(newErrors);
      return false;
    }
    setDetailErrors({});
    return true;
  };

  const fillDetailsFromDonor = useCallback(
    (donor: DonationMaster, phone: string) => {
      setDetails(prev => ({
        ...prev,
        name: donor.name ?? "",
        email: donor.email ?? "",
        phone: donor.phone ?? phone,
        organization: donor.organization ?? "",
        panNumber: donor.pan_number ?? "",
        message: donor.message ?? "",
        addressLine1: donor.address_line1 ?? "",
        addressLine2: donor.address_line2 ?? "",
        state_id: String(donor.state_id ?? donor.state ?? ""),
        district_id: String(donor.district_id ?? donor.district ?? ""),
        city_id: String(donor.city_id ?? donor.city ?? ""),
        pincode: donor.pincode ?? "",
      }));
    },
    []
  );

  const handleStep1Continue = async () => {
    if (!validateStep1()) {
      return;
    }

    const phone = step1Mobile.replace(/\D/g, "").slice(0, 10);
    if (phone.length !== 10) {
      notify.error(
        "Invalid mobile",
        "Please enter a valid 10-digit mobile number."
      );
      return;
    }

    if (showOtpField) {
      if (otp.trim().length < 6) {
        notify.error("Invalid OTP", "Please enter the 6-digit OTP.");
        return;
      }
      if (!codeReference) {
        notify.error(
          "Session expired",
          "Please verify your mobile number again."
        );
        setShowOtpField(false);
        return;
      }

      setMobileFlowLoading(true);
      try {
        const response = await donorAuthAPI.verifyOTP(
          otp,
          codeReference,
          otpAuthMode,
          phone
        );
        if (!response?.access) {
          notify.error(
            "Verification failed",
            "Invalid or expired OTP. Please try again."
          );
          return;
        }
        setDetails(prev => ({ ...prev, phone }));
        setShowOtpField(false);
        setOtp("");
        setOtpAuthMode("register");
        setStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (err) {
        if (err instanceof DonorLoginRequiredError) {
          setOtpAuthMode("login");
          setCodeReference(err.code_reference);
          setOtp("");
          restartOtpTimer();
          notify.info("Account found", err.message);
          return;
        }
        notify.error(
          "Verification failed",
          err instanceof Error
            ? err.message
            : "OTP verification failed. Please try again."
        );
      } finally {
        setMobileFlowLoading(false);
      }
      return;
    }

    setMobileFlowLoading(true);
    try {
      const donor = await donationMasterAPI.searchByPhone(phone);
      if (donor) {
        fillDetailsFromDonor(donor, phone);
        setStep(2);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }

      const otpResponse = await donorAuthAPI.sendOTP(phone);
      setCodeReference(otpResponse.code_reference);
      setOtpAuthMode(otpResponse.mode);
      setShowOtpField(true);
      setOtp("");
      restartOtpTimer();
      notify.success(
        otpResponse.mode === "login" ? "Login OTP sent" : "OTP sent",
        otpResponse.message ||
        `OTP sent to +91 ${phone}. Enter it below to continue.`
      );
    } catch (err) {
      notify.error(
        "Error",
        err instanceof Error
          ? err.message
          : "Unable to verify mobile number. Please try again."
      );
    } finally {
      setMobileFlowLoading(false);
    }
  };

  const handleContinue = () => {
    if (step === 1) {
      void handleStep1Continue();
      return;
    }
    if (step === 2 && validateStep2()) {
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleResendOtp = async () => {
    if (otpResendSeconds > 0 || resendingOtp || mobileFlowLoading) {
      return;
    }

    const phone = step1Mobile.replace(/\D/g, "").slice(0, 10);
    if (phone.length !== 10) {
      notify.error(
        "Invalid mobile",
        "Please enter a valid 10-digit mobile number."
      );
      return;
    }

    if (!codeReference) {
      notify.error(
        "Session expired",
        "Please verify your mobile number again."
      );
      setShowOtpField(false);
      return;
    }

    setResendingOtp(true);
    try {
      const response = await donorAuthAPI.resendOTP(phone, codeReference);
      if (response.code_reference) {
        setCodeReference(response.code_reference);
      }
      setOtp("");
      restartOtpTimer();
      notify.success(
        "OTP resent",
        response.message || `A new OTP has been sent to +91 ${phone}.`
      );
    } catch (err) {
      try {
        const otpResponse = await donorAuthAPI.sendOTP(phone);
        setCodeReference(otpResponse.code_reference);
        setOtpAuthMode(otpResponse.mode);
        setOtp("");
        restartOtpTimer();
        notify.success(
          otpResponse.mode === "login" ? "Login OTP sent" : "OTP sent",
          otpResponse.message ||
          `OTP sent to +91 ${phone}. Enter it below to continue.`
        );
      } catch (fallbackErr) {
        notify.error(
          "Resend failed",
          fallbackErr instanceof Error
            ? fallbackErr.message
            : err instanceof Error
              ? err.message
              : "Unable to resend OTP. Please try again."
        );
      }
    } finally {
      setResendingOtp(false);
    }
  };

  const handleBack = () => {
    if (step === 1) {
      navigate("/donation");
      return;
    }
    if (step === 2) {
      setShowOtpField(false);
      setOtp("");
      setCodeReference("");
    }
    setStep((step - 1) as Step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePayment = useCallback(async () => {
    if (!campaign) {
      return;
    }
    if (!getRazorpayKey()) {
      notify.error(
        "Configuration Error",
        "Razorpay is not configured. Please set VITE_RAZORPAY_KEY_ID."
      );
      return;
    }

    try {
      setSubmitting(true);
      const schemeId = Number(campaign.id);
      const createPayload: DonationCreateOrderPayload = {
        scheme_id: Number.isFinite(schemeId) ? schemeId : campaign.id,
        name: details.name.trim(),
        email: details.email.trim(),
        phone: details.phone.trim(),
        donation_type: campaign.typeOfDonationName ?? campaign.title,
        amount: amount.toFixed(2),
        organization: details.organization.trim() || undefined,
        pan_number: details.panNumber.trim() || undefined,
        aadhaar_number: "",
        message: details.message.trim() || undefined,
        address_line1: details.addressLine1.trim(),
        address_line2: details.addressLine2.trim() || undefined,
        state: details.state_id,
        district: details.district_id,
        city: details.city_id,
        pincode: details.pincode.trim(),
        mode: "online",
      };

      const orderResponse = await donationsAPI.createOrder(createPayload);
      const orderId = orderResponse.order_id;
      const amountPaise =
        typeof orderResponse.amount === "number"
          ? orderResponse.amount
          : Math.round(amount * 100);

      openRazorpayCheckout({
        order_id: orderId,
        amount: amountPaise,
        currency: orderResponse.currency ?? "INR",
        name: "JBF Donation",
        description: `Donation - ${campaign.title}`,
        onSuccess: async response => {
          setVerifying(true);
          setVerificationStatus("verifying");
          await new Promise(r => setTimeout(r, 0));
          try {
            const res = response as unknown as Record<string, unknown>;
            const razorpay_payment_id =
              response.razorpay_payment_id ?? res.razorpay_payment_id;
            const razorpay_signature =
              response.razorpay_signature ??
              res.razorpay_signature ??
              res.signature;
            if (!razorpay_payment_id || !razorpay_signature) {
              notify.error(
                "Verification Failed",
                "Invalid payment response from Razorpay."
              );
              return;
            }
            await donationsAPI.verifyPayment({
              razorpay_order_id: orderId,
              razorpay_payment_id: String(razorpay_payment_id),
              razorpay_signature: String(razorpay_signature),
            });

            const addPayload: DonationAddPayload = {
              name: details.name.trim(),
              email: details.email.trim(),
              phone: details.phone.trim(),
              organization: details.organization.trim(),
              donation_type: campaign.typeOfDonationName ?? campaign.title,
              amount: amount.toFixed(2),
              mode: "online",
              payment_mode: "razorpay",
              pan_number: details.panNumber.trim(),
              aadhaar_number: null,
              message: details.message.trim(),
              address_line1: details.addressLine1.trim(),
              address_line2: details.addressLine2.trim(),
              state: details.state_id,
              district: details.district_id,
              city: details.city_id,
              pincode: details.pincode.trim(),
            };

            try {
              await donationsAPI.addDonation(addPayload);
            } catch (addErr) {
              console.error("Donation add error:", addErr);
              notify.error(
                "Donation recorded",
                "Payment verified, but donation record sync failed. Please contact support if needed."
              );
            }

            setVerificationStatus("success");
            await new Promise(r => setTimeout(r, 1500));
            navigate("/donation");
          } catch (verifyErr) {
            console.error("Verify payment error:", verifyErr);
            notify.error(
              "Verification Failed",
              verifyErr instanceof Error
                ? verifyErr.message
                : "Payment received but verification failed. Please contact support."
            );
          } finally {
            setVerifying(false);
            setVerificationStatus("idle");
          }
        },
      });
    } catch (error) {
      console.error(error);
      notify.error(
        "Payment Error",
        "Unable to start payment. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }, [campaign, details, amount, navigate]);

  if (loadingCampaign) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center bg-[#FFF9F0] px-4">
        <Loader2 className="h-10 w-10 animate-spin text-[#7A1E1E]" />
        <p className="mt-4 text-gray-600">Loading campaign...</p>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center bg-[#FFF9F0] px-4">
        <p className="mb-4 text-gray-600">Campaign not found.</p>
        <Link
          to="/donation"
          className="text-[#7A1E1E] underline hover:text-[#5A1515]"
        >
          Back to all campaigns
        </Link>
      </div>
    );
  }

  const steps = [
    { num: 1, label: "Amount", shortLabel: "Amount" },
    { num: 2, label: "Your Details", shortLabel: "Details" },
    { num: 3, label: "Payment", shortLabel: "Pay" },
  ] as const;

  return (
    <div className="min-h-screen bg-[#FFF9F0]">
      <PaymentVerificationOverlay
        open={verifying}
        status={verificationStatus}
      />
      {/* Hero */}
      <section className="bg-[#7A1E1E] text-white">
        <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
          <div className="mb-6 flex flex-wrap items-center gap-3 sm:gap-4">
            <Link
              to="/donation"
              className="inline-flex items-center gap-2 text-sm font-medium text-white/85 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 shrink-0" />
              Back to all campaigns
            </Link>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1fr_min(380px,100%)] lg:items-start lg:gap-10">
            <div className="min-w-0">
              <span className="rounded-full bg-gradient-to-r from-[#D4A017] to-[#B8900F] px-3.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white sm:text-xs">
                {getCampaignStatusLabel(campaign.dateStatus)}
              </span>
              <h1 className="mt-3 text-3xl font-bold leading-tight sm:text-4xl lg:text-[2.5rem]">
                {campaign.title}
              </h1>
              {campaign.subTitle && (
                <p className="mt-2 text-lg text-[#D4A017]/95">
                  {campaign.subTitle}
                </p>
              )}
            </div>

            {campaign.goal > 0 && (
              <div className="rounded-2xl border border-white/15 bg-black/15 p-5 backdrop-blur-sm sm:p-6">
                <p className="text-3xl font-bold tracking-tight sm:text-4xl">
                  {formatInr(campaign.raised)}
                </p>
                <p className="mt-0.5 text-sm text-white/75">
                  of {formatInr(campaign.goal)}
                </p>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/15">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#D4A017] to-[#E8B84A]"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="mt-2.5 flex justify-between text-xs font-medium text-white/80">
                  <span>{progress}% funded</span>
                  <span>{formatInr(stillNeeded)} still needed</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stepper */}
      <div className="border-b border-[#7A1E1E]/5 bg-[#FFF9F0] py-5 sm:py-8">
        <ol className="mx-auto flex w-full max-w-2xl list-none items-center justify-between px-3 sm:justify-center sm:px-4">
          {steps.map((s, i) => {
            const isComplete = step > s.num;
            const isCurrent = step === s.num;
            const isUpcoming = step < s.num;

            return (
              <li
                key={s.num}
                className={`flex min-w-0 items-center ${i < steps.length - 1 ? "flex-1" : "shrink-0"
                  }`}
              >
                <div className="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-3">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold sm:h-10 sm:w-10 sm:text-sm ${isCurrent
                        ? "bg-[#D4A017] text-white ring-2 ring-[#D4A017]/25 sm:ring-4"
                        : isComplete
                          ? "bg-[#D4A017] text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                  >
                    {isComplete ? (
                      <Check className="h-4 w-4 stroke-[2.5] sm:h-5 sm:w-5" />
                    ) : (
                      s.num
                    )}
                  </div>
                  <span
                    className={`min-w-0 text-[11px] font-semibold leading-tight sm:whitespace-nowrap sm:text-sm ${isCurrent
                        ? "text-[#7A1E1E]"
                        : isUpcoming
                          ? "text-gray-400"
                          : "text-[#7A1E1E]/70"
                      }`}
                  >
                    <span className="sm:hidden">{s.shortLabel}</span>
                    <span className="hidden sm:inline">{s.label}</span>
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`mx-1.5 h-px min-w-2 flex-1 sm:mx-4 sm:w-16 sm:flex-none md:w-20 ${step > s.num ? "bg-[#D4A017]/50" : "bg-gray-200"
                      }`}
                    aria-hidden
                  />
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Main content */}
      <div className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-3 lg:items-start lg:gap-8">
          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-gray-100/80 bg-white p-6 shadow-[0_4px_24px_rgba(122,30,30,0.06)] sm:p-8 lg:p-10">
              {step === 1 && (
                <>
                  <h2 className="mb-1 text-2xl font-bold text-[#7A1E1E] sm:text-[1.65rem]">
                    Choose your contribution
                  </h2>
                  <p className="mb-5 text-sm font-medium text-gray-600">
                    {campaign.amountType === "fixed"
                      ? "Contribution amount (₹)"
                      : "Select an amount (₹)"}
                  </p>
                  <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
                    {contributionAmounts.map(optionAmount => {
                      const selected = amount === optionAmount;
                      return (
                        <button
                          key={optionAmount}
                          type="button"
                          onClick={() => selectAmount(optionAmount)}
                          className={`flex items-center justify-center rounded-xl border px-2 py-3.5 text-sm font-semibold transition-all sm:py-4 sm:text-base ${selected
                              ? "border-[#D4A017] bg-[#FFF8EB] text-[#7A1E1E] shadow-sm"
                              : "border-gray-200 bg-white text-gray-800 hover:border-gray-300"
                            }`}
                        >
                          ₹{optionAmount.toLocaleString("en-IN")}
                        </button>
                      );
                    })}
                  </div>
                  {campaign.amountType === "variable" && (
                    <input
                      type="text"
                      inputMode="numeric"
                      value={customAmount}
                      onChange={e => handleCustomAmountChange(e.target.value)}
                      className="mb-6 w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-base text-gray-900 focus:border-[#D4A017] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/20"
                      placeholder="Enter custom amount"
                      aria-label="Custom donation amount"
                    />
                  )}
                  {campaign.amountType === "fixed" && (
                    <input
                      type="text"
                      readOnly
                      value={customAmount}
                      className="mb-6 w-full cursor-default rounded-xl border border-[#D4A017]/30 bg-[#FFF8EB] px-4 py-3.5 text-base font-semibold text-[#7A1E1E]"
                      aria-label="Fixed donation amount"
                    />
                  )}
                  <div>
                    <label
                      htmlFor="step1Mobile"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="step1Mobile"
                      value={step1Mobile}
                      onChange={e => {
                        setStep1Mobile(
                          e.target.value.replace(/\D/g, "").slice(0, 10)
                        );
                        setShowOtpField(false);
                        setOtp("");
                        setCodeReference("");
                      }}
                      maxLength={10}
                      disabled={mobileFlowLoading}
                      placeholder="Enter 10-digit mobile number"
                      className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:border-[#7A1E1E] focus:outline-none focus:ring-2 focus:ring-[#7A1E1E]/20 disabled:bg-gray-50"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      We will look up your details or verify via OTP before
                      continuing.
                    </p>
                  </div>

                  {showOtpField && (
                    <div className="mt-5 rounded-2xl border border-[#7A1E1E]/15 bg-[#FFF9F2] p-4 sm:p-6">
                      <div className="mb-4 flex items-start gap-2.5 sm:gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#7A1E1E]/10 text-[#7A1E1E] sm:h-11 sm:w-11">
                          <KeyRound className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <label className="block text-sm font-semibold text-gray-800">
                            Enter OTP <span className="text-red-500">*</span>
                          </label>
                          <p className="mt-1 text-xs leading-relaxed text-gray-600 sm:text-sm">
                            {otpAuthMode === "login" ? "Login OTP" : "OTP"} sent
                            to{" "}
                            <span className="font-medium text-[#7A1E1E]">
                              +91 {step1Mobile}
                            </span>
                            . Enter the 6-digit code to continue.
                          </p>
                        </div>
                      </div>

                      <div className="py-1 sm:py-2">
                        <InputOTP
                          maxLength={6}
                          value={otp}
                          onChange={setOtp}
                          disabled={mobileFlowLoading}
                          containerClassName="w-full"
                        >
                          <InputOTPGroup className="mx-auto max-w-md sm:max-w-none">
                            {[0, 1, 2, 3, 4, 5].map(index => (
                              <InputOTPSlot key={index} index={index} />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                      </div>

                      <p
                        className={`mt-3 text-center text-xs font-medium ${otp.length === 6 ? "text-green-600" : "text-gray-500"
                          }`}
                      >
                        {otp.length === 6
                          ? "OTP entered. Tap continue to verify."
                          : `${otp.length}/6 digits entered`}
                      </p>

                      {otpResendSeconds > 0 ? (
                        <p className="mt-2 text-center text-xs text-gray-500">
                          Resend OTP in{" "}
                          <span className="font-semibold text-[#7A1E1E]">
                            {formatOtpTimer(otpResendSeconds)}
                          </span>
                        </p>
                      ) : (
                        <div className="mt-2 text-center">
                          <button
                            type="button"
                            onClick={() => void handleResendOtp()}
                            disabled={resendingOtp || mobileFlowLoading}
                            className="text-xs font-semibold text-[#7A1E1E] transition-colors hover:text-[#5A1515] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {resendingOtp ? "Sending OTP..." : "Resend OTP"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {step === 2 && (
                <>
                  <h2 className="mb-6 text-2xl font-bold text-[#7A1E1E]">
                    Your details
                  </h2>
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <label
                        htmlFor="donorName"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="donorName"
                        name="name"
                        value={details.name}
                        onChange={handleDetailChange}
                        className={`${detailInputClass} ${detailErrors.name ? detailInputErr : detailInputOk
                          }`}
                        placeholder="Enter your name"
                      />
                      {detailErrors.name && (
                        <p className="mt-1 text-xs text-red-500">
                          {detailErrors.name}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label
                          htmlFor="donorEmail"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="donorEmail"
                          name="email"
                          value={details.email}
                          onChange={handleDetailChange}
                          className={`${detailInputClass} ${detailErrors.email ? detailInputErr : detailInputOk
                            }`}
                          placeholder="Enter email address"
                        />
                        {detailErrors.email && (
                          <p className="mt-1 text-xs text-red-500">
                            {detailErrors.email}
                          </p>
                        )}
                      </div>
                      <div>
                        <label
                          htmlFor="donorPhone"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="donorPhone"
                          name="phone"
                          value={details.phone}
                          onChange={handleDetailChange}
                          maxLength={10}
                          className={`${detailInputClass} ${detailErrors.phone ? detailInputErr : detailInputOk
                            }`}
                          placeholder="Enter 10-digit phone number"
                        />
                        {detailErrors.phone && (
                          <p className="mt-1 text-xs text-red-500">
                            {detailErrors.phone}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="organization"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Organization/Company
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={details.organization}
                        onChange={handleDetailChange}
                        className={`${detailInputClass} ${detailInputOk}`}
                        placeholder="Enter organization name (optional)"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="panNumber"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        PAN Number <span className="text-red-500">*</span>
                        {amount > 2000 && (
                          <span className="font-normal text-gray-500">
                            {" "}
                            (required for 80G above ₹2,000)
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        id="panNumber"
                        name="panNumber"
                        value={details.panNumber}
                        onChange={handleDetailChange}
                        className={`${detailInputClass} ${detailErrors.panNumber
                            ? detailInputErr
                            : detailInputOk
                          }`}
                        placeholder="Enter PAN Number"
                      />
                      {detailErrors.panNumber && (
                        <p className="mt-1 text-xs text-red-500">
                          {detailErrors.panNumber}
                        </p>
                      )}
                    </div>

                    <div className="space-y-4 rounded-xl border border-[#7A1E1E]/10 p-4 sm:p-5">
                      <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#7A1E1E]">
                        Address Information
                      </h3>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label
                            htmlFor="addressLine1"
                            className="mb-1.5 block text-xs font-semibold text-gray-700"
                          >
                            Address Line 1{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="addressLine1"
                            name="addressLine1"
                            value={details.addressLine1}
                            onChange={handleDetailChange}
                            className={`${detailInputClass} ${detailErrors.addressLine1
                                ? detailInputErr
                                : detailInputOk
                              }`}
                            placeholder="House No, Building, Street"
                          />
                          {detailErrors.addressLine1 && (
                            <p className="mt-1 text-xs text-red-500">
                              {detailErrors.addressLine1}
                            </p>
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="addressLine2"
                            className="mb-1.5 block text-xs font-semibold text-gray-700"
                          >
                            Address Line 2 (Optional)
                          </label>
                          <input
                            type="text"
                            id="addressLine2"
                            name="addressLine2"
                            value={details.addressLine2}
                            onChange={handleDetailChange}
                            className={`${detailInputClass} ${detailInputOk}`}
                            placeholder="Area, Landmark"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label
                            htmlFor="state"
                            className="mb-1.5 block text-xs font-semibold text-gray-700"
                          >
                            State <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              id="state"
                              name="state_id"
                              value={details.state_id}
                              onChange={handleDetailChange}
                              className={`w-full appearance-none rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 ${detailErrors.state_id
                                  ? detailInputErr
                                  : "border-gray-200 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                                }`}
                            >
                              <option value="">
                                {loadingStates ? "Loading..." : "Select State"}
                              </option>
                              {states.map(s => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                              <ChevronDown className="h-4 w-4" />
                            </div>
                          </div>
                          {detailErrors.state_id && (
                            <p className="mt-1 text-xs text-red-500">
                              {detailErrors.state_id}
                            </p>
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="district"
                            className="mb-1.5 block text-xs font-semibold text-gray-700"
                          >
                            District <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              id="district"
                              name="district_id"
                              value={details.district_id}
                              onChange={handleDetailChange}
                              disabled={!details.state_id || loadingDistricts}
                              className={`w-full appearance-none rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-50 ${detailErrors.district_id
                                  ? detailInputErr
                                  : "border-gray-200 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                                }`}
                            >
                              <option value="">
                                {loadingDistricts
                                  ? "Loading..."
                                  : "Select District"}
                              </option>
                              {districts.map(d => (
                                <option key={d.id} value={d.id}>
                                  {d.name}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                              <ChevronDown className="h-4 w-4" />
                            </div>
                          </div>
                          {detailErrors.district_id && (
                            <p className="mt-1 text-xs text-red-500">
                              {detailErrors.district_id}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <label
                            htmlFor="city"
                            className="mb-1.5 block text-xs font-semibold text-gray-700"
                          >
                            City <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <select
                              id="city"
                              name="city_id"
                              value={details.city_id}
                              onChange={handleDetailChange}
                              disabled={!details.district_id || loadingCities}
                              className={`w-full appearance-none rounded-lg border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 disabled:bg-gray-50 ${detailErrors.city_id
                                  ? detailInputErr
                                  : "border-gray-200 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                                }`}
                            >
                              <option value="">
                                {loadingCities ? "Loading..." : "Select City"}
                              </option>
                              {cities.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400">
                              <ChevronDown className="h-4 w-4" />
                            </div>
                          </div>
                          {detailErrors.city_id && (
                            <p className="mt-1 text-xs text-red-500">
                              {detailErrors.city_id}
                            </p>
                          )}
                        </div>
                        <div>
                          <label
                            htmlFor="pincode"
                            className="mb-1.5 block text-xs font-semibold text-gray-700"
                          >
                            Pincode <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id="pincode"
                            name="pincode"
                            value={details.pincode}
                            onChange={handleDetailChange}
                            maxLength={6}
                            className={`${detailInputClass} ${detailErrors.pincode
                                ? detailInputErr
                                : detailInputOk
                              }`}
                            placeholder="Enter 6-digit pincode"
                          />
                          {detailErrors.pincode && (
                            <p className="mt-1 text-xs text-red-500">
                              {detailErrors.pincode}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="donorMessage"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Message (optional)
                      </label>
                      <textarea
                        id="donorMessage"
                        name="message"
                        value={details.message}
                        onChange={handleDetailChange}
                        rows={4}
                        className={`${detailInputClass} ${detailInputOk}`}
                        placeholder="Any note for this donation (optional)"
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <h2 className="mb-4 text-2xl font-bold text-[#7A1E1E]">
                    Complete payment
                  </h2>
                  <p className="mb-6 text-gray-600">
                    Pay securely via Razorpay (cards, UPI, net banking) or use
                    the UPI ID in the sidebar.
                  </p>
                </>
              )}

              <div className="mt-10 flex flex-col-reverse gap-3 border-t border-gray-100 pt-8 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={submitting || verifying}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#7A1E1E] px-6 py-2.5 text-base font-semibold text-[#7A1E1E] transition-colors hover:bg-[#7A1E1E] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {step === 1 ? "Back to campaigns" : "Back"}
                </button>

                {step < 3 ? (
                  <button
                    type="button"
                    onClick={handleContinue}
                    disabled={
                      submitting ||
                      verifying ||
                      mobileFlowLoading ||
                      import.meta.env.VITE_DISABLE_DONATION === "true"
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#B8900F] px-8 py-2.5 text-base font-semibold text-white shadow-md shadow-[#D4A017]/25 transition-transform hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-80"
                  >
                    {mobileFlowLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {step === 1 && showOtpField
                          ? "Verifying..."
                          : "Please wait..."}
                      </>
                    ) : (
                      <>
                        {step === 1 && showOtpField
                          ? "Verify OTP & Continue"
                          : "Continue"}
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handlePayment}
                    disabled={
                      submitting ||
                      verifying ||
                      import.meta.env.VITE_DISABLE_DONATION === "true"
                    }
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#B8900F] px-6 py-3.5 text-base font-semibold text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-80 sm:px-8"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Pay {formatInr(amount)} via Razorpay
                        <Heart className="h-5 w-5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          <DonationDetailSidebar
            campaign={campaign}
            amount={amount}
            step={step}
            upiId={UPI_ID}
            copiedUpi={copiedUpi}
            onCopyUpi={handleCopyUpi}
          />
        </div>
      </div>
    </div>
  );
}
